// S-1 `/` — Landing. Source: stage-4-screens.md §5.1 + прототип sA (index.html).
//
// Логика разделения unauth vs прочие state'ы — в OnboardingRouter
// (см. App.tsx wire-up). Этот компонент рендерит ТОЛЬКО UI Landing'а (unauth-branch).
//
// Структура 1:1 по прототипу #sA: sticky header → hero (+ social proof + image) →
// stats → «Как это работает» (3 шага) → категории → сравнение → безопасность →
// final CTA → footer. Все CTA ведут на наши роуты /signup и /signin.
//
// ВНИМАНИЕ (см. прототип, блок «Открытые вопросы»): цифры в stats/social-proof
// (50K+, 15K+, 99.9%, ★★★★★) — placeholder. Финальные тексты и реальные числа
// даёт Бакыт + маркетинг. До тех пор лендинг считаем non-production контентом.

import { Link } from 'wouter';
import {
  Activity,
  BarChart3,
  Check,
  Heart,
  KeyRound,
  Layers,
  Lightbulb,
  Lock,
  ShieldCheck,
  Sparkles,
  Star,
  UploadCloud,
  X,
  Zap,
} from 'lucide-react';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { IconTile } from '@/components/ui/icon-tile';
import { Surface } from '@/components/ui/surface';
import { ListRow, ListRowGroup } from '@/components/ui/list-row';
import { LEGAL_CONTACT_EMAIL } from '@/content/legal';
import heroImage from '@/assets/hero-health-atlas.webp';

type Step = { icon: typeof Activity; n: number; title: string; text: string };
const steps: Step[] = [
  {
    icon: UploadCloud,
    n: 1,
    title: 'Загрузите результаты',
    text: 'PDF, фото или скан — мы распознаём большинство форматов анализов за несколько секунд',
  },
  {
    icon: Layers,
    n: 2,
    title: 'Получите структуру',
    text: 'Все показатели автоматически распознаются и группируются по категориям',
  },
  {
    icon: Lightbulb,
    n: 3,
    title: 'Следите и улучшайте',
    text: 'Графики динамики, персональные подсказки и напоминания о проверках',
  },
];

/**
 * HEAL-13: сколько маркеров обещаем на лендинге. Одна константа на все три места
 * (бейдж, блок цифр, таблица сравнения) — раньше они жили порознь и разъезжались.
 *
 * Число сверяется с реальным каталогом тестом Landing.markersClaim.test.ts: если
 * каталог усохнет ниже обещанного, тест упадёт. При росте каталога число можно
 * поднять вручную — это маркетинговая формулировка, а не счётчик.
 *
 * HEAL-97: «500+» поставлено по решению PM 08.10.2026 по каталогу врача (504),
 * хотя на проде на тот момент залито около 237 — заливка остальных идёт в HEAL-86/99.
 */
export const MARKERS_CLAIM = '500+';

// Числа с карточек убраны (решение PM 05.08.2026): в каталоге 18 систем организма,
// а на лендинге четыре укрупнённые группы — любые счётчики здесь разошлись бы с тем,
// что человек видит внутри приложения.
type Category = { icon: typeof Activity; title: string };
const categories: Category[] = [
  { icon: Heart, title: 'Сердце и метаболизм' },
  { icon: Activity, title: 'Гормоны и щитовидка' },
  { icon: Sparkles, title: 'Витамины и микроэлементы' },
  { icon: Zap, title: 'Энергия и восстановление' },
];

type SecurityCard = { icon: typeof Activity; title: string; text: string };
const securityCards: SecurityCard[] = [
  { icon: Lock, title: 'AES-256 шифрование', text: 'На уровне современных банковских систем' },
  { icon: ShieldCheck, title: 'Конфиденциальность', text: 'Доступ к данным строго ограничен' },
  { icon: KeyRound, title: 'Ваши данные — ваши', text: 'Не передаём третьим лицам без согласия' },
];

type Stat = { value: string; label: string };
const stats: Stat[] = [
  { value: '50K+', label: 'Анализов загружено' },
  { value: '15K+', label: 'Пользователей' },
  { value: MARKERS_CLAIM, label: 'Показателей здоровья' },
  { value: '99.9%', label: 'Точность распознавания' },
];

type CompareRow = { label: string; ours: 'check' | string; theirs: 'cross' | string };
const compareRows: CompareRow[] = [
  { label: 'Показателей', ours: MARKERS_CLAIM, theirs: '~10–15' },
  { label: 'Подсказки по образу жизни', ours: 'check', theirs: 'cross' },
  { label: 'Графики динамики', ours: 'check', theirs: 'cross' },
  { label: 'Безопасное хранение', ours: 'check', theirs: 'Частично' },
];

export function Landing() {
  return (
    <MobileFrame>
      {/* Sticky header */}
      <header className="sticky top-0 z-10 flex h-14 flex-shrink-0 items-center justify-between border-b border-border bg-background/90 px-4 backdrop-blur-sm">
        <div className="text-base font-bold tracking-tight">
          Health<span className="text-primary-text">Lab</span>
        </div>
        {/* HEAL-53: на первом экране была ВТОРАЯ зелёная кнопка «Начать», ведущая
            туда же, куда и главный CTA («Загрузить первый анализ» → /signup). Два
            равнозначных действия = выбор без разницы. Оставляем один главный CTA,
            а шапку отдаём под вход — его на лендинге не было вовсе, и вернувшийся
            пользователь упирался в регистрацию. */}
        <Link
          href="/signin"
          className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground"
        >
          Войти
        </Link>
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10 px-6 pb-12 pt-10">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative">
            <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-hl-small font-medium shadow-sm">
              <Star className="h-3 w-3 fill-current text-primary-text" />
              Новый стандарт заботы о здоровье
            </div>
            {/* HEAL-7: тексты hero по ТЗ «Медицинский атлас» и финальному макету.
                Заголовок — основной вариант ТЗ (бьёт по существующей боли); вариант
                «Все анализы — в одной карте здоровья» остаётся кандидатом для A/B. */}
            <h1 className="mb-4 text-[34px] font-bold leading-[1.1] tracking-tight">
              Не ищите изменения в десятках анализов
            </h1>
            <p className="mb-6 text-base leading-relaxed text-muted-foreground">
              HealthLab собирает результаты в одну историю и показывает, что требует внимания.
            </p>
            <div className="mb-5 space-y-2.5">
              <Link
                href="/signup"
                className="block w-full rounded-control bg-primary py-3 text-center text-sm font-semibold text-primary-foreground"
              >
                Загрузить первый анализ
              </Link>
              <a
                href="#how"
                className="block w-full rounded-control border border-border bg-background py-3 text-center text-sm font-semibold"
              >
                Как это работает
              </a>
            </div>
            <p className="mb-5 text-hl-small leading-snug text-muted-foreground">
              Сервис не ставит диагнозы и не заменяет консультацию врача. Информация носит справочный характер и помогает вам лучше понимать свои данные.
            </p>
            {/* Social proof — placeholder, финальный контент даёт Бакыт */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex -space-x-2">
                {['A', 'B', 'C', 'D'].map((l) => (
                  <div
                    key={l}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium text-muted-foreground"
                  >
                    {l}
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                <div className="flex gap-0.5 text-primary-text">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3 w-3 fill-current" />
                  ))}
                </div>
                <div>15 000+ пользователей</div>
              </div>
            </div>
          </div>
          {/* Иллюстрация «Медицинский атлас» (HEAL-7). Из макета взята только графика:
              логотип, заголовок и строка доверия в нём впечатаны, а у нас они живые —
              вставь макет целиком, на экране было бы по два заголовка. */}
          <img
            src={heroImage}
            alt="Карта здоровья HealthLab: показатели анализов собраны в одну историю"
            width={1100}
            height={966}
            className="mt-8 w-full rounded-surface shadow-xl"
            loading="eager"
          />

          {/* Строка доверия под визуалом (ТЗ §5) */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 flex-shrink-0 text-primary-text" aria-hidden="true" />
            Логика создана вместе с медицинскими экспертами
          </div>
        </section>

        {/* Stats — placeholder цифры */}
        {/* §2.4: было четыре числа без разделителей — сетка читалась как четыре
            отдельных плаката. Линия в один пиксель делает её таблицей.
            Реализация: gap 1px на подложке цвета рамки — обычный border дал бы
            двойные линии на стыках. По центру метрики остаются: число само по
            себе колонка, и здесь это оправдано (§2.1). */}
        <section className="border-y border-border bg-muted/30 px-6 py-10">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-surface bg-border">
            {stats.map((s) => (
              <div key={s.label} className="bg-muted/30 px-4 py-5 text-center">
                <div className="mb-0.5 text-2xl font-bold">{s.value}</div>
                <div className="text-hl-small text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="px-6 py-12">
          <div className="mb-8">
            <span className="mb-3 inline-block rounded-full bg-muted px-3 py-1 text-xs font-medium">
              Просто и понятно
            </span>
            <h2 className="mb-2 text-2xl font-bold">Как это работает</h2>
            <p className="text-sm text-muted-foreground">
              Три простых шага к более осознанному подходу к своему здоровью
            </p>
          </div>
          <div className="space-y-4">
            {steps.map(({ icon: Icon, n, title, text }) => (
              /* §2.2: было — иконка по центру, под ней заголовок и текст, тоже
                 по центру. Три таких блока подряд читались как три плаката, а
                 не как пронумерованные шаги. Номер на углу плитки — приём из
                 прототипа Алины, он остаётся. */
              <Surface key={n} className="flex items-start gap-4 p-5">
                <div className="relative flex-shrink-0">
                  <IconTile size="lg">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </IconTile>
                  <div className="absolute -right-1.5 -top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {n}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1.5 font-semibold">{title}</h3>
                  <p className="text-hl-small leading-relaxed text-muted-foreground">{text}</p>
                </div>
              </Surface>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="bg-muted/30 px-6 py-12">
          <div className="mb-8">
            <span className="mb-3 inline-block rounded-full bg-background px-3 py-1 text-xs font-medium">
              {MARKERS_CLAIM} показателей
            </span>
            <h2 className="mb-2 text-2xl font-bold">Полный контроль здоровья</h2>
            <p className="text-sm text-muted-foreground">
              Отслеживайте ключевые показатели — от сердца и гормонов до витаминов и восстановления
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {categories.map(({ icon: Icon, title }) => (
              /* h-full: у категорий названия разной длины, и без него соседи
                 в строке получались разной высоты. */
              <Surface key={title} className="flex h-full flex-col p-4">
                <IconTile size="md" className="mb-3">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </IconTile>
                <h3 className="text-sm font-semibold leading-tight">{title}</h3>
              </Surface>
            ))}
          </div>
        </section>

        {/* Comparison */}
        <section className="px-6 py-12">
          <div className="mb-6">
            <span className="mb-3 inline-block rounded-full bg-muted px-3 py-1 text-xs font-medium">
              Сравнение
            </span>
            <h2 className="mb-2 text-2xl font-bold">Не обычное приложение</h2>
            <p className="text-sm text-muted-foreground">Сравните с традиционными трекерами</p>
          </div>
          <Surface className="overflow-hidden">
            <div className="grid grid-cols-3 border-b border-border p-3 text-hl-small">
              <div className="font-medium text-muted-foreground">Возможности</div>
              <div className="text-center font-bold text-primary-text">HealthLab</div>
              <div className="text-center font-medium text-muted-foreground">Обычные</div>
            </div>
            {compareRows.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-3 items-center p-3 text-hl-small ${
                  i < compareRows.length - 1 ? 'border-b border-border/60' : ''
                }`}
              >
                <div>{row.label}</div>
                <div className="text-center">
                  {row.ours === 'check' ? (
                    <span className="mx-auto flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  ) : (
                    <span className="font-medium text-primary-text">{row.ours}</span>
                  )}
                </div>
                <div className="text-center text-muted-foreground">
                  {row.theirs === 'cross' ? (
                    <span className="mx-auto flex h-4 w-4 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <X className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  ) : (
                    row.theirs
                  )}
                </div>
              </div>
            ))}
          </Surface>
        </section>

        {/* Security */}
        <section className="px-6 py-12">
          <div className="mb-6">
            <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-medium">
              <ShieldCheck className="h-3 w-3" />
              Безопасность
            </span>
            <h2 className="mb-2 text-2xl font-bold">Ваши данные под защитой</h2>
            <p className="text-sm text-muted-foreground">
              Современные методы защиты и международные стандарты
            </p>
          </div>
          {/* §2.4: три пункта по строке текста были завёрнуты в три карточки с
              иконкой по центру. Это перечисление, а не три самостоятельных
              объекта, — значит список с волосяными разделителями. */}
          <ListRowGroup>
            {securityCards.map(({ icon: Icon, title, text }) => (
              <ListRow
                key={title}
                icon={
                  <IconTile size="sm">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </IconTile>
                }
                title={title}
                description={text}
              />
            ))}
          </ListRowGroup>
        </section>

        {/* Final CTA */}
        <section className="bg-primary px-6 py-14 text-center text-primary-foreground">
          <h2 className="mb-3 text-2xl font-bold leading-tight">
            Начните заботиться о здоровье осознанно
          </h2>
          <p className="mb-6 text-sm leading-relaxed text-primary-foreground/85">
            Присоединяйтесь к тысячам людей, которые уже контролируют своё здоровье с HealthLab
          </p>
          <div className="space-y-2">
            <Link
              href="/signup"
              className="block w-full rounded-control bg-background py-3 text-center text-sm font-semibold text-primary-text"
            >
              Создать аккаунт бесплатно →
            </Link>
            <Link
              href="/signin"
              className="block w-full rounded-control border border-primary-foreground/30 bg-transparent py-3 text-center text-sm font-semibold text-primary-foreground"
            >
              Войти
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 px-6 py-8 text-hl-small text-gray-400">
          <div className="mb-2 text-base font-bold text-white">
            Health<span className="text-primary-text">Lab</span>
          </div>
          <p className="mb-4 leading-relaxed">
            HealthLab не заменяет консультацию врача. При отклонениях обратитесь к специалисту.
          </p>
          <div className="mb-3 flex flex-wrap gap-3">
            {/* HEAL-11: подписи были по-английски (Privacy/Terms) при русском интерфейсе.
                Названия совпадают с заголовками самих документов — иначе человек жмёт одно,
                а открывается другое. */}
            <Link href="/legal/privacy" className="hover:text-white">
              Политика конфиденциальности
            </Link>
            <Link href="/legal/tos" className="hover:text-white">
              Условия использования
            </Link>
            <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="hover:text-white">
              Контакты
            </a>
          </div>
          <p>© 2026 HealthLab</p>
        </footer>
      </main>
    </MobileFrame>
  );
}
